import type { DesignScene } from '$src/domain/designs/DesignScene';
import { BoxGeometry, BufferGeometry, DoubleSide, Line, LineBasicMaterial, LineSegments, MathUtils, Mesh, MeshBasicMaterial, MeshPhongMaterial, MeshStandardMaterial, OrthographicCamera, Plane, PlaneGeometry, Vector3 } from 'three';
import { GraphicsContext } from './GraphicsContext'
import { OrthographicContext } from './OrthographicContext';
import { OrbitControls, TeapotGeometry } from 'three/examples/jsm/Addons.js';
import { ProjectionGenerator } from '../core/edge-projection';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { CabinetS20Test } from '$src/CGS/renderer/Meshes/tests/CabinetS20Test';
import { CabinetS1Test } from '$src/CGS/renderer/Meshes/tests/CabinetS1Test';
import { CabinetOvenTest } from '$src/CGS/renderer/Meshes/tests/CabinetOvenTest';
import { ISideViewCanvas } from '$src/domain/designs/editor/ISideViewCanvas';
import { CabinetS1PulloutTest } from '$src/CGS/renderer/Meshes/tests/CabinetS1PullOuts';
import { TextTest } from './TextTest';
import Stats from 'three/examples/jsm/libs/stats.module.js';


export class SideViewCanvas implements ISideViewCanvas {

    public graphicsContext : GraphicsContext;
    public orbitControl: OrbitControls;

    public designScene: DesignScene;

    private stats: Stats

    constructor(designScene: DesignScene, divContainer: HTMLElement) {
  
        this.designScene = designScene;
        this.graphicsContext = this.createGraphicsContext(divContainer);
        this.orbitControl = new OrbitControls(this.graphicsContext.camera, divContainer);
        this.orbitControl.enablePan = true;
        this.orbitControl.enableRotate = false;
        this.orbitControl.zoomToCursor = true;

        this.stats = new Stats();
        document.body.appendChild(this.stats.dom);
    }

    private createGraphicsContext(divContainer: HTMLElement) : GraphicsContext {
        return OrthographicContext.createDefault(divContainer);
    }

    public async initialize() {

        this.addCube();
        //await this.addTeapot();
        this.addTest();
        this.render();
    }

    public async addTest() {

        // var x: MeshTest = new MeshTest();
        // this.graphicsContext.scene.add(x.object3D);



        const x5 = new TextTest();
        x5.init(this.graphicsContext.scene);

        for(var i=0; i< 100; i++) {
            var x10 = new CabinetS1PulloutTest().getCabinet();
            if( x10 ) {
                x10.position.set(Math.random() * 10 - 5, 0, Math.random() * 10 - 5);
                this.graphicsContext.scene.add(x10);
            }
        }

        var x0 = new CabinetS1PulloutTest().getCabinet();
        if( x0 ) {
            x0.position.set(-0.6,0,0);
            this.graphicsContext.scene.add(x0);
        }
        
        var x1 = new CabinetS1Test().getCabinet();
        if( x1 ) {
            x1.position.set(0.76,0,0);
            this.graphicsContext.scene.add(x1);
        }

        var x2 = new CabinetS1Test().getCabinet();
        if( x2 ) {
            x2.position.set(1.36,0,0);
            this.graphicsContext.scene.add(x2);
        }

        var x3 = new CabinetS20Test().getCabinet();
        if( x3 ) {
            x3.position.set(2.58,0, 0.28);
            this.graphicsContext.scene.add(x3);
        }

        var x = new CabinetOvenTest().getCabinet();
        if( x ) {
            this.graphicsContext.scene.add(x);

            const geometry = new PlaneGeometry(20, 20);

            const material = new MeshStandardMaterial({ color: 0xffffff }); // Couleur verte

            const floor = new Mesh(geometry, material);
            floor.receiveShadow = true;

            floor.rotation.x = -Math.PI / 2; // Rotation de -90 degrés sur l'axe X

            this.graphicsContext.scene.add(floor);


            // const lineMaterial = new LineBasicMaterial({color: '#000', side: DoubleSide, linewidth: 0.5 })
    
            // const generator = new ProjectionGenerator();
            // generator.includeIntersectionEdges = false;
            // generator.iterationTime = 30;
            // generator.angleThreshold = 50;
            // generator.sortEdges = true;
            
            // const result : BufferGeometry = await generator.generateAsync( x.Box?.MeshInfo );
             
            // const mesh = new LineSegments( result, lineMaterial );
            // mesh.position.setX(1)
            // this.graphicsContext.scene.add( mesh );

        }
    }

    public async addTeapot() {
        // const geometry = new TeapotGeometry( 2 ,
        //     8,
        //     true,
        //     true,
        //     true,
        //     true);
        
        // const material = new MeshPhongMaterial({ color: 0xffff00 });

        // geometry.rotateX(-90);

        // var teapot = new Mesh( geometry, material );
        // teapot.position.set(1,-1,1);
        //teapot.rotateX(90);

        //this.graphicsContext.scene.add( teapot );


        // Création d'un chargeur GLTFLoader
        const loader = new GLTFLoader();

        // Chemin vers le modèle GLTF
        const modelPath = 'scene.gltf';

        // Chargement du modèle GLTF
        loader.load( modelPath, async (gltf) => {

            let model = gltf.scene;

            this.graphicsContext.scene.add(model);

            // transform and merge geometries to project into a single model
            const geometries = [];
            model.updateWorldMatrix( true, true );
            model.traverse( c => {
    
                if ( c.geometry ) {
    
                    const clone = c.geometry.clone();
                    clone.applyMatrix4( c.matrixWorld );
                    for ( const key in clone.attributes ) {
    
                        if ( key !== 'position' ) {
    
                            clone.deleteAttribute( key );
    
                        }
    
                    }
    
                    geometries.push( clone );
    
                }
    
            } );
            const mergedGeometry = mergeGeometries( geometries, false );
            
            mergedGeometry.rotateX(90);

            const lineMaterial = new LineBasicMaterial({color: '#000', side: DoubleSide, linewidth: 0.5 })
    
            const generator = new ProjectionGenerator();
            generator.includeIntersectionEdges = false;
            generator.iterationTime = 30;
            generator.angleThreshold = 50;
            generator.sortEdges = true;
            
            const result : BufferGeometry = await generator.generateAsync( mergedGeometry );
             
            const mesh = new LineSegments( result, lineMaterial );
            this.graphicsContext.scene.add( mesh );

        });
    }

    public async addCube() {
    
        const geometry = new BoxGeometry(1, 1, 1);
        const material = new MeshPhongMaterial({ color: 0x00ff00 });
        let cube = new Mesh(geometry, material);
        cube.translateZ(-1);

        //this.graphicsContext.scene.add(cube);


        const points = [];
        points.push( new Vector3( -1, 0, 0 ) );
        points.push( new Vector3( 1, 0, 0 ) );
        //points.push( new Vector3( 1, 0, 0 ) );

        // const lineMaterial = new LineBasicMaterial( { color: 0x0000ff } );

        // const geometryLine = new BufferGeometry().setFromPoints( points );
        // const line = new Line( geometryLine, lineMaterial );

        // this.graphicsContext.scene.add(line);
    }

    
    render() {
        requestAnimationFrame(() => this.render());

        this.graphicsContext.resizeCanvasToDisplaySize();
        this.graphicsContext.renderer.render(this.graphicsContext.scene, this.graphicsContext.camera);

        this.stats.update();
    }
}